'use client';
import React from 'react';
import { Check, X } from 'lucide-react';

const PasswordStrengthIndicator = ({ password, validation, showCriteria = true }) => {
  if (!password) {
    return null;
  }

  const { strength, strengthLevel, strengthColor, criteria } = validation;

  // Define strength level styles
  const getStrengthBarStyle = () => {
    let bgClass = 'bg-gray-200';
    let fillClass = 'bg-gray-400';
    
    switch (strengthLevel) {
      case 'weak':
        fillClass = 'bg-red-500';
        break;
      case 'fair':
        fillClass = 'bg-orange-500';
        break;
      case 'medium':
        fillClass = 'bg-yellow-500';
        break;
      case 'strong':
        fillClass = 'bg-green-500';
        break;
      default:
        fillClass = 'bg-gray-400';
    }
    
    return { bgClass, fillClass };
  };

  const { bgClass, fillClass } = getStrengthBarStyle();

  // Get strength label
  const getStrengthLabel = () => {
    const labels = {
      weak: 'Weak',
      fair: 'Fair',
      medium: 'Good',
      strong: 'Strong'
    };
    return labels[strengthLevel] || 'Too weak';
  };

  return (
    <div className="mt-2 space-y-2">
      {/* Strength Bar */}
      <div className="space-y-1">
        <div className="flex items-center justify-between">
          <span className="text-xs font-medium text-gray-700">Password strength</span>
          <span 
            className="text-xs font-semibold"
            style={{ color: strengthColor }}
          >
            {getStrengthLabel()}
          </span>
        </div>
        <div className={`h-2 rounded-full ${bgClass} overflow-hidden`}>
          <div 
            className={`h-full rounded-full transition-all duration-300 ${fillClass}`}
            style={{ width: `${strength}%` }}
          />
        </div>
      </div>

      {/* Criteria Checklist - Filter out noRepeating and notCommon */}
      {showCriteria && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 mt-3">
          {Object.entries(criteria)
            .filter(([key]) => key !== 'noRepeating' && key !== 'notCommon')
            .map(([key, criterion]) => (
              <div 
                key={key}
                className={`flex items-center gap-1.5 text-xs transition-all duration-200 ${
                  criterion.passed ? 'text-green-600' : 'text-gray-500'
                }`}
              >
                {criterion.passed ? (
                  <Check className="w-3.5 h-3.5 flex-shrink-0" />
                ) : (
                  <div className="w-3.5 h-3.5 rounded-full border border-gray-400 flex-shrink-0" />
                )}
                <span className={criterion.passed ? 'font-medium' : ''}>
                  {criterion.message}
                </span>
              </div>
            ))}
        </div>
      )}
    </div>
  );
};

export default PasswordStrengthIndicator;